import React, { createContext, useContext, useRef, useState, useEffect } from 'react';

const BotContext = createContext();
export const useBot = () => useContext(BotContext);

function getUserKey() {
    const user = JSON.parse(localStorage.getItem('usuarioLogado'));
    return user && user.email ? `prisma_bot_state_${user.email}` : null;
}

export const BotProvider = ({ children }) => {
    // Carregar estado salvo do bot do usuário
    const userKey = getUserKey();
    let initialState = {};
    if (userKey) {
        try {
            initialState = JSON.parse(localStorage.getItem(userKey)) || {};
        } catch (e) { initialState = {}; }
    }
    const [isRunning, setIsRunning] = useState(initialState.isRunning || false);
    const [status, setStatus] = useState(initialState.status || 'AGUARDANDO');
    const [statusColor, setStatusColor] = useState(initialState.statusColor || '#CBD5E1');
    const [statusPulse, setStatusPulse] = useState(initialState.statusPulse || false);
    const [timeLeft, setTimeLeft] = useState(initialState.timeLeft || 30 * 60);
    const [logs, setLogs] = useState(initialState.logs || [{ time: new Date().toLocaleTimeString('pt-BR', { timeStyle: 'short' }), message: '> Sistema inicializado.', className: '' }]);
    const [trades, setTrades] = useState(initialState.trades || []);
    // Garantir que chartData comece com apenas um valor inicial
    // Saldo inicial do usuário logado
    let saldoInicial = 0;
    try {
        const user = JSON.parse(localStorage.getItem('usuarioLogado'));
        if (user && !isNaN(Number(user.saldoInicial))) saldoInicial = Number(user.saldoInicial);
    } catch (e) { }
    let safeChartData = Array.isArray(initialState.chartData)
        ? initialState.chartData.map(v => Number(v)).filter(v => !isNaN(v))
        : [];
    // O gráfico deve começar com o saldo inicial do usuário
    if (safeChartData.length === 0) safeChartData = [saldoInicial];
    const [chartData, setChartData] = useState(safeChartData);

    // Refs e ciclo do bot
    const currentRoundRef = useRef(0);
    const resultsPoolRef = useRef([]);
    const timerIntervalRef = useRef(null);
    const roundTimeoutRef = useRef(null);
    const marketBreathIntervalRef = useRef(null);
    const animationFrameRef = useRef(null);
    const liveValueRef = useRef(0);
    const targetLiveValueRef = useRef(0);
    const lastHistoryValueRef = useRef(0);
    const MAX_ROUNDS = 11;
    const CYCLE_DURATION = 30 * 60;

    // Métodos globais do ciclo do bot
    const addLog = (message, className = '') => {
        const time = new Date().toLocaleTimeString('pt-BR', { timeStyle: 'short' });
        setLogs(prev => {
            const newLogs = [...prev, { time, message, className }];
            return newLogs.slice(-5);
        });
    };

    const updateStatus = (text, color, pulse) => {
        setStatus(text);
        setStatusColor(color);
        setStatusPulse(pulse);
    };

    const addTrade = (asset, value, type) => {
        const time = new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
        setTrades(prev => [{ asset, value, type, time }, ...prev]);
    };



    const runTimer = () => {
        timerIntervalRef.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    finishCycle();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const runNextRound = (balance, onBalanceUpdate) => {
        if (currentRoundRef.current >= MAX_ROUNDS) {
            finishCycle();
            return;
        }
        updateStatus('ANALISANDO', '#00ff15', true);
        addLog('Analisando mercado...', 'text-muted');
        roundTimeoutRef.current = setTimeout(() => {
            const isWin = resultsPoolRef.current[currentRoundRef.current];
            const assets = ['EUR/USD', 'BTC/USD', 'XAU/USD', 'GBP/USD', 'SP500'];
            const asset = assets[Math.floor(Math.random() * assets.length)];
            const amount = Math.random() * (50 - 20) + 20;
            let newBalance = balance;
            let newAvailableBalance = balance;
            let diff = 0;
            if (isWin) {
                const profit = amount * 0.85;
                newBalance += profit;
                newAvailableBalance += profit;
                diff = profit;
                addLog(`✅ WIN em ${asset}: +$${profit.toFixed(2)}`, 'text-success');
                addTrade(asset, `+$${profit.toFixed(2)}`, 'win');
            } else {
                newBalance -= amount;
                newAvailableBalance -= amount;
                diff = -amount;
                addLog(`❌ LOSS em ${asset}: -$${amount.toFixed(2)}`, 'text-danger');
                addTrade(asset, `-$${amount.toFixed(2)}`, 'loss');
            }
            // Atualiza o gráfico com o saldo real após a operação
            const saldoNum = Number(newBalance);
            setChartData(prev => [...prev, Number(saldoNum.toFixed(2))]);
            if (onBalanceUpdate) onBalanceUpdate(newBalance, newAvailableBalance);
            lastHistoryValueRef.current = newBalance;
            currentRoundRef.current++;
            runNextRound(newBalance, onBalanceUpdate);
        }, 160000); // 2 minutos e 40 segundos
    };

    const startCycle = (balance, onBalanceUpdate) => {
        setIsRunning(true);
        currentRoundRef.current = 0;
        resultsPoolRef.current = [1, 1, 1, 1, 1, 1, 1, 1, 0, 0, 0].sort(() => Math.random() - 0.5);
        updateStatus('INICIANDO', '#F59E0B', true);
        addLog('Iniciando protocolo de operações...', 'text-warning');
        lastHistoryValueRef.current = balance;
        setChartData(prev => {
            const numBalance = Number(balance);
            if (prev.length === 0 || prev[prev.length - 1] !== numBalance) {
                return [...prev, numBalance];
            }
            return prev;
        });
        marketBreathIntervalRef.current = setInterval(() => {
            const drift = (Math.random() - 0.5) * 0.5;
            targetLiveValueRef.current = lastHistoryValueRef.current + drift;
        }, 4000);
        setTimeLeft(CYCLE_DURATION);
        runTimer();
        runNextRound(balance, onBalanceUpdate);
    };

    const stopCycle = (silent = false) => {
        if (!silent && !window.confirm('Deseja pausar o ciclo?')) return;
        setIsRunning(false);
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        if (marketBreathIntervalRef.current) clearInterval(marketBreathIntervalRef.current);
        if (roundTimeoutRef.current) clearTimeout(roundTimeoutRef.current);
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        updateStatus('PAUSADO', '#CBD5E1', false);
        if (!silent) addLog('⏸ Operação pausada pelo usuário', 'text-muted');
    };

    const finishCycle = () => {
        setIsRunning(false);
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        if (marketBreathIntervalRef.current) clearInterval(marketBreathIntervalRef.current);
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        updateStatus('FINALIZADO', '#00C689', false);
        addLog('🏁 Ciclo finalizado com sucesso', 'text-success');
        setTimeLeft(CYCLE_DURATION);
    };

    // Salvar estado do bot sempre que mudar
    useEffect(() => {
        if (!userKey) return;
        // Filtrar logs, trades e chartData para garantir que só dados simples sejam salvos
        const safeLogs = logs.map(l => ({
            time: l.time,
            message: l.message,
            className: l.className || ''
        }));
        const safeTrades = trades.map(t => ({
            asset: t.asset,
            value: t.value,
            type: t.type,
            time: t.time
        }));
        const safeChartData = Array.isArray(chartData) ? chartData.filter(v => typeof v === 'number' || typeof v === 'string') : [];
        localStorage.setItem(userKey, JSON.stringify({
            isRunning, status, statusColor, statusPulse, timeLeft, logs: safeLogs, trades: safeTrades, chartData: safeChartData
        }));
    }, [isRunning, status, statusColor, statusPulse, timeLeft, logs, trades, chartData, userKey]);

    return (
        <BotContext.Provider value={{
            isRunning, status, statusColor, statusPulse, timeLeft, logs, trades, chartData,
            startCycle, stopCycle, finishCycle
        }}>
            {children}
        </BotContext.Provider>
    );
}
